import { convexQuery } from "@convex-dev/react-query";
import { api } from "@selca/backend/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@workspace/ui/components/button";
import { ChevronLeftIcon, MailIcon, PhoneIcon } from "lucide-react";
import { useNavigation } from "../App";
import { useAuth } from "../hooks/use-auth";
import { authClient } from "../lib/auth-client";
import { LoggedOutView } from "./LoggedOutView";

export function SettingsPage() {
  const { navigate } = useNavigation();
  const queryClient = useQueryClient();
  /** 🔐 Unified auth state */
  const { session, isAuthenticated, isAuthLoading } = useAuth();

  const { data: myAccounts, isLoading: isLoadingAccounts } = useQuery({
    ...convexQuery(api.users.getMyAccounts, {}),
    enabled: isAuthenticated && !isAuthLoading,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const isGoogleLinked = myAccounts?.some(
    (a: { providerId: string }) => a.providerId === "google"
  );

  const hasAccountsData = myAccounts !== undefined;
  const isLoading = isLoadingAccounts || isAuthLoading || !isAuthenticated;

  async function handleLinkGoogle() {
    await authClient.linkSocial({
      provider: "google",
      callbackURL: window.location.origin + "/settings",
    });
  }

  if (!session) {
    return <LoggedOutView />;
  }

  return (
    <div className="w-full max-w-2xl space-y-8 py-10">
      <div className="flex items-center gap-4">
        <Button
          className="rounded-full"
          onClick={() => navigate("/")}
          size="icon-sm"
          variant="ghost"
        >
          <ChevronLeftIcon className="size-4" />
        </Button>
        <h1 className="font-bold font-display text-3xl tracking-tight">
          Settings
        </h1>
      </div>

      <div className="space-y-6">
        <section className="space-y-4">
          <h2 className="font-semibold text-lg">Account Information</h2>
          <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-muted">
                <PhoneIcon className="size-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Phone Number
                </p>
                <p className="font-medium">
                  {session.user.phoneNumber || "Not linked"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-muted">
                <MailIcon className="size-5 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Email
                </p>
                <p className="font-medium">
                  {session.user.email || "No email provided"}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">Backups</h2>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-full bg-muted">
                  <svg className="size-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Google</p>
                  <p className="text-xs text-muted-foreground">
                    {isLoading
                      ? "Loading..."
                      : isGoogleLinked
                        ? "Linked as backup"
                        : "Add Google as a backup sign-in method"}
                  </p>
                </div>
              </div>
              {hasAccountsData && (
                <Button
                  disabled={isGoogleLinked}
                  onClick={handleLinkGoogle}
                  size="sm"
                  variant={isGoogleLinked ? "outline" : "default"}
                >
                  {isGoogleLinked ? "Linked" : "Link Account"}
                </Button>
              )}
            </div>
          </div>
        </section>

        <section className="pt-4">
          <Button
            className="w-full sm:w-auto"
            onClick={async () => {
              await authClient.signOut();
              // Force session refresh to update reactive state
              await authClient.getSession();
              // Clear all cached queries
              queryClient.clear();
              // Wait for reactive session state to propagate before navigating
              await new Promise((resolve) => setTimeout(resolve, 150));
              navigate("/");
            }}
            variant="destructive"
          >
            Sign Out
          </Button>
        </section>
      </div>
    </div>
  );
}
